/**
 * Bloque `onSave`/`onSaveConfiguration` tant qu'un document chargé dans un format obsolète
 * (`Shape.formatVersion` — voir BESOINS.md §8.x) n'a pas été explicitement migré par l'utilisateur.
 * Logique pure (aucun DOM ici, voir `ui/MigrationDialog.ts` pour le dialogue lui-même et
 * `createTokaMap.ts` pour le câblage) — `requestMigration` encapsule à la fois l'affichage du
 * dialogue ET l'appel à `TokaMapConfig.onMigrateDocument` en cas d'acceptation ; ça permet à
 * `ensureMigrated()` de ne connaître que "accepté" (`true`) / "refusé" (`false`), sans savoir
 * comment la décision est obtenue.
 *
 * Une seule instance est partagée entre `onSave` ET `onSaveConfiguration` (câblés dans
 * `createTokaMap.ts`) : c'est le MÊME document dont le format est en cause, donc une seule
 * décision doit s'appliquer aux deux.
 */
export class FormatMigrationGate {
  private migrated: boolean;
  private pendingResult: Promise<boolean> | null = null;

  constructor(
    alreadyLatest: boolean,
    private requestMigration: () => Promise<boolean>,
  ) {
    this.migrated = alreadyLatest;
  }

  /**
   * `true` si le document est (déjà, ou vient d'être) migré — l'appelant peut alors procéder
   * normalement à `onSave`/`onSaveConfiguration`. `false` si l'utilisateur a refusé : l'appelant
   * doit faire échouer sa propre sauvegarde (ex. rejeter sa promesse) pour déclencher le rollback
   * déjà existant de `ShapeSaveTracker`/`ConfigurationSaveTracker` — cette classe ne fait aucun
   * rollback elle-même.
   *
   * Plusieurs appels concurrents (ex. plusieurs formes sauvegardées en même temps lors d'une
   * édition de sélection multiple, voir BESOINS.md §13) partagent la MÊME décision : un seul
   * dialogue est affiché, un seul appel à `onMigrateDocument` est fait, même si `ensureMigrated()`
   * est appelée plusieurs fois avant que la première résolution n'arrive. Un refus réinitialise
   * l'état : le prochain appel (après résolution du précédent) relance un nouveau dialogue.
   */
  async ensureMigrated(): Promise<boolean> {
    if (this.migrated) return true;
    if (!this.pendingResult) {
      this.pendingResult = this.requestMigration()
        .then((accepted) => {
          if (accepted) this.migrated = true;
          return accepted;
        })
        .finally(() => {
          this.pendingResult = null;
        });
    }
    return this.pendingResult;
  }
}
